import Papa from 'papaparse';
import { genders } from 'constants/campaign';
import { CampaignApplication } from 'types/campaign/applications';
import { SocialAccountDemographicInsights } from 'types/influencer';
import { getSocialAccountProfileURL } from 'utils/helpers';

const TOP_AUDIENCE_LIMIT = 3;

const BASE_COLUMNS = [
    'name',
    'mobile',
    'handle',
    'url',
    'follower_count',
    'engagement_score',
    'age',
    'location',
    'gender',
    'average_views',
] as const;

const COLUMNS_LABELS = {
    name: 'Name',
    mobile: 'Mobile',
    handle: 'Handle',
    url: 'URL',
    follower_count: 'Followers',
    engagement_score: 'Engagement Score',
    age: 'Age',
    location: 'Location',
    gender: 'Gender',
    average_views: 'Average Views',
    audience_gender: 'Audience Gender',
    audience_age: 'Audience Age',
    audience_location: 'Audience Location',
};

interface DemographicEntry {
    key: string;
    label: string;
    value: number;
}

const genderLabel = (key: string): string => {
    const normalized = key.trim().length === 1 ? key.trim().toUpperCase() : key.trim();
    return (genders as Record<string, string>)[normalized] ?? key;
};

const sanitizeColumnKey = (label: string): string =>
    label.replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_');

const getDemographic = (application: CampaignApplication): SocialAccountDemographicInsights => {
    const platform = application.social_account?.platform;
    if (platform === 'yt') {
        return application.social_account?.yt?.insights?.demographic ?? {};
    }
    return application.social_account?.ig?.insights?.demographic ?? {};
};

const getTopEntries = (
    data?: Record<string, number>,
    limit = TOP_AUDIENCE_LIMIT,
    labelFn: (key: string) => string = (key) => key
): DemographicEntry[] => {
    if (!data) return [];

    return Object.entries(data)
        .filter(([, value]) => typeof value === 'number' && Number.isFinite(value) && value > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([key, value]) => ({
            key,
            label: labelFn(key),
            value,
        }));
};

const getTopLocationEntries = (demographic: SocialAccountDemographicInsights): DemographicEntry[] => {
    const merged: Record<string, number> = {
        ...(demographic.country ?? {}),
        ...(demographic.city ?? {}),
    };
    return getTopEntries(merged, TOP_AUDIENCE_LIMIT);
};

const getAudienceColumns = (applications: CampaignApplication[]): string[] => {
    const audienceColumnSet = new Set<string>();

    applications.forEach((application) => {
        const demographic = getDemographic(application);

        getTopEntries(demographic.gender, TOP_AUDIENCE_LIMIT, genderLabel).forEach((entry) => {
            audienceColumnSet.add(`audience_gender_${sanitizeColumnKey(entry.label)}`);
        });
        getTopEntries(demographic.age, TOP_AUDIENCE_LIMIT).forEach((entry) => {
            audienceColumnSet.add(`audience_age_${sanitizeColumnKey(entry.label)}`);
        });
        getTopLocationEntries(demographic).forEach((entry) => {
            audienceColumnSet.add(`audience_location_${sanitizeColumnKey(entry.label)}`);
        });
    });

    return Array.from(audienceColumnSet).sort();
};

const applicationToCsvRow = (
    application: CampaignApplication,
    columns: string[]
): Record<string, string | number> => {
    const influencer = application.influencer;
    const socialAccount = application.social_account;
    const demographic = getDemographic(application);

    const row: Record<string, string | number> = {
        name: influencer?.name ?? socialAccount?.name ?? '',
        mobile: influencer?.mobile ?? '',
        handle: socialAccount?.handle ?? '',
        url: socialAccount?.handle
            ? getSocialAccountProfileURL(socialAccount.handle, socialAccount.platform)
            : '',
        follower_count: socialAccount?.followers_count ?? '',
        engagement_score: socialAccount?.engagement_score ?? '',
        age: influencer?.age ?? '',
        location: influencer?.location ?? socialAccount?.location ?? '',
        gender: influencer?.gender_name ?? '',
        average_views: application.average_views ?? '',
    };

    // columns.forEach((column) => {
    //     row[column] = '';
    // });

    // getTopEntries(demographic.gender, TOP_AUDIENCE_LIMIT, genderLabel).forEach((entry) => {
    //     row[`audience_gender_${sanitizeColumnKey(entry.label)}`] = entry.value;
    // });
    // getTopEntries(demographic.age, TOP_AUDIENCE_LIMIT).forEach((entry) => {
    //     row[`audience_age_${sanitizeColumnKey(entry.label)}`] = entry.value;
    // });
    // getTopLocationEntries(demographic).forEach((entry) => {
    //     row[`audience_location_${sanitizeColumnKey(entry.label)}`] = entry.value;
    // });

    return row;
};

export const buildApplicationsCsv = (applications: CampaignApplication[]): string => {
    // Use column labels for CSV headers instead of slugs
    const columns = [...BASE_COLUMNS];
    const columnLabels = columns.map(col => COLUMNS_LABELS[col as keyof typeof COLUMNS_LABELS] ?? col);

    const rows = applications.map((application) => {
        const row = applicationToCsvRow(application, []);
        return columns.map((column) => row[column] ?? '');
    });

    return Papa.unparse({ fields: columnLabels, data: rows });
};

export const downloadCsvFile = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
};
